import type { UiDuel } from '@/hooks/data/duels';

/**
 * True when at least one duel player has an FPL availability flag
 * (chance of playing < 100). Mirrors AvailabilityBadge show-condition
 * and server-side `DUELS_HAS_FLAG_EXPRESSION`. Flagged duels sink to
 * the end of the list regardless of vote count.
 */
export function hasAvailabilityFlag(duel: Pick<UiDuel, 'player1' | 'player2'>): boolean {
  const isFlagged = (p: Pick<UiDuel['player1'], 'availability_pct'>): boolean => {
    const pct = p.availability_pct;
    return pct !== null && pct !== undefined && pct !== 100;
  };
  return isFlagged(duel.player1) || isFlagged(duel.player2);
}

function getTotalSelectedByPercent(duel: Pick<UiDuel, 'player1' | 'player2'>): number {
  const p1 = parseFloat(duel.player1.selectedByPercent || '0') || 0;
  const p2 = parseFloat(duel.player2.selectedByPercent || '0') || 0;
  return p1 + p2;
}

/**
 * Comparator for duels listing: DOTW first, then non-flagged before flagged,
 * then by total votes desc, then by total selected_by_percent desc.
 */
export function compareDuelsForListing(a: UiDuel, b: UiDuel): number {
  if (a.isDuelOfTheWeek && !b.isDuelOfTheWeek) return -1;
  if (!a.isDuelOfTheWeek && b.isDuelOfTheWeek) return 1;

  const aFlagged = hasAvailabilityFlag(a);
  const bFlagged = hasAvailabilityFlag(b);
  if (aFlagged && !bFlagged) return 1;
  if (!aFlagged && bFlagged) return -1;

  const votesDiff = b.votes.total_votes - a.votes.total_votes;
  if (votesDiff !== 0) return votesDiff;

  return getTotalSelectedByPercent(b) - getTotalSelectedByPercent(a);
}
